/**
 * Balance Sheet Report (UI-17 v3 layout, BE-17 data)
 *
 * Standard A = L + E format with "As of" date selector. Each section lists its accounts from the ledger; equity adds
 * retained earnings and the year's net profit. Banded styling like the P&L, full width.
 * Route: /reports/balance-sheet
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Alert, Chip, Skeleton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { CurrencyText, ReportExportMenu } from '../../components/accounting';
import { formatDate } from '../../utils/accounting/formatters';
import { toYmd } from '../../utils/accounting/periodUtils';
import { balanceSheetService } from '../../services/accounting/balanceSheet.service';
import { serverMessage } from '../../utils/apiConvert';

/* ── helpers ──────────────────────────────────────────────── */

export const SectionHeader = ({ children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mt: 1.5, py: 0.6, px: 1.5, borderLeft: 3, borderColor: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
      <Typography sx={{ fontSize: fs.xs, fontWeight: fw.bold, color: 'primary.main', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {children}
      </Typography>
    </Box>
  );
};

const SubHeader = ({ children }) => (
  <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, color: 'text.secondary', mt: 1, mb: 0.25, px: 1.5 }}>
    {children}
  </Typography>
);

export const LineItem = ({ label, amount, indent = 1, onClick }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      title={onClick ? 'Open this account in the General Ledger' : undefined}
      sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, px: 1.5, cursor: onClick ? 'pointer' : 'default', '&:hover': { bgcolor: theme.palette.action.hover } }}
    >
      <Typography sx={{ fontSize: fs.sm, color: 'text.secondary', pl: indent * 2 }}>{label}</Typography>
      <CurrencyText amount={amount} size="sm" withPrefix={false} />
    </Box>
  );
};

export const TotalLine = ({ label, amount }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', py: 0.5, px: 1.5, mt: 0.25,
      borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.text.primary, 0.03),
    }}>
      <Typography sx={{ fontSize: fs.sm, fontWeight: fw.bold }}>{label}</Typography>
      <CurrencyText amount={amount} size="sm" bold withPrefix={false} />
    </Box>
  );
};

export const ResultBand = ({ label, amount }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      mt: 1.5, py: 0.8, px: 1.5, display: 'flex', justifyContent: 'space-between',
      bgcolor: alpha(theme.palette.primary.main, 0.08), borderTop: 1, borderBottom: 1, borderColor: alpha(theme.palette.primary.main, 0.35),
    }}>
      <Typography sx={{ fontSize: fs.md, fontWeight: fw.bold }}>{label}</Typography>
      <CurrencyText amount={amount} size="md" bold withPrefix={false} />
    </Box>
  );
};

// Each account line opens that account in the General Ledger, from 1 January to the sheet's date.
const Lines = ({ section, asOfDate }) => {
  const navigate = useNavigate();
  return (section?.lines || []).map((l) => (
    <LineItem key={l.account} label={l.name} amount={l.amount}
      onClick={() => navigate(`/reports/general-ledger?account=${encodeURIComponent(l.account)}&from=${asOfDate.slice(0, 4)}-01-01&to=${asOfDate}`)} />
  ));
};

/* ── component ──────────────────────────────────────────── */

const BalanceSheet = () => {
  const [asOfDate, setAsOfDate] = useState(toYmd(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!asOfDate) return;
    setLoading(true);
    setError(null);
    try {
      setData(await balanceSheetService.get({ asOfDate }));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load balance sheet'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => { load(); }, [load]);

  const assets = data?.assets;
  const liabilities = data?.liabilities;
  const equity = data?.equity;
  const hasFixed = (assets?.fixedAssets?.lines || []).length > 0;

  return (
    <Box sx={{ p: 2, width: 0, minWidth: '100%', boxSizing: 'border-box' }}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>Balance Sheet</Typography>
            {data && (
              <Typography sx={{ fontSize: fs.xs, color: 'text.secondary' }}>
                As of {formatDate(data.asOfDate)} · {data.basis === 'CASH' ? 'Cash' : 'Accrual'} basis · year from {formatDate(data.yearStart)}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label="As of" type="date" size="small" value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />
            <ReportExportMenu reportType="balance-sheet" params={{ to: asOfDate }} onError={setError} />
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <Box>
            {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} height={28} sx={{ mt: 1 }} />)}
          </Box>
        ) : !data ? (
          !error && <Alert severity="info">No balance sheet available for this date.</Alert>
        ) : (
          <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, pb: 1.5, overflow: 'hidden' }}>
            {/* ASSETS */}
            <SectionHeader>Assets</SectionHeader>
            <SubHeader>Current Assets</SubHeader>
            <Lines section={assets.currentAssets} asOfDate={data.asOfDate} />
            <TotalLine label="Total Current Assets" amount={assets.currentAssets.total} />

            {hasFixed && (
              <>
                <SubHeader>Fixed Assets</SubHeader>
                <Lines section={assets.fixedAssets} asOfDate={data.asOfDate} />
                <TotalLine label="Total Fixed Assets" amount={assets.fixedAssets.total} />
              </>
            )}

            <ResultBand label="TOTAL ASSETS" amount={assets.total} />

            {/* LIABILITIES */}
            <SectionHeader>Liabilities</SectionHeader>
            <SubHeader>Current Liabilities</SubHeader>
            <Lines section={liabilities.currentLiabilities} asOfDate={data.asOfDate} />
            <TotalLine label="Total Liabilities" amount={liabilities.total} />

            {/* EQUITY */}
            <SectionHeader>Equity</SectionHeader>
            <Lines section={equity} asOfDate={data.asOfDate} />
            <LineItem label="Retained Earnings" amount={equity.retainedEarnings} />
            <LineItem label="Net Profit (YTD)" amount={equity.netProfitYtd} />
            <TotalLine label="Total Equity" amount={equity.total} />

            {/* TOTAL L+E */}
            <ResultBand label="TOTAL LIABILITIES + EQUITY" amount={data.totalLiabilitiesAndEquity} />

            {/* Balance check */}
            <Box sx={{ mt: 1.5, px: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                label={data.balanced ? 'Balanced' : `NOT Balanced — difference ${Number(data.difference || 0).toFixed(2)}`}
                size="small"
                sx={{
                  bgcolor: data.balanced ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                  color: data.balanced ? '#22c55e' : '#ef4444',
                  fontWeight: fw.semibold,
                  fontSize: fs.xs,
                }}
              />
            </Box>
          </Box>
        )}

        {data?.notes?.length > 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {data.notes.map((n) => <Typography key={n} sx={{ fontSize: fs.xs }}>{n}</Typography>)}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default BalanceSheet;
